import isChromatic from 'chromatic';
import classnames from 'classnames';
import { Variants, motion } from 'framer-motion';
import * as React from 'react';
import { useWizard } from 'react-use-wizard';

const getVariants = (animateOpacity: boolean): Variants => ({
  enter: (direction: number) => {
    return {
      x: direction > 0 ? '5%' : '-5%',
      opacity: !animateOpacity ? 1 : 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? '5%' : '-5%',
      opacity: !animateOpacity ? 1 : 0,
    };
  },
});
type Props = {
  setPreviousStep: React.Dispatch<React.SetStateAction<number>>;
  previousStep: number;
  children: React.ReactElement;
  className?: string;
};
/**
 * @framerSupportedLayoutHeight fixed
 */
export const AnimatedWizardStep = ({
  children,
  className,
  previousStep,
  setPreviousStep,
}: Props) => {
  const { activeStep } = useWizard();

  // This is necessary to tell framer motion which direction to animate
  // in our steps. If we are moving forward the previous step is < the current
  // otherwise > than the current
  React.useEffect(() => {
    return () => {
      setPreviousStep(activeStep);
    };
  }, [activeStep, setPreviousStep]);

  // We don't want to change opacity when rendering these steps in Chromatic
  // the opacity change takes time and it will cause stories to be
  // flaky if the text is only sometimes opaque
  const variants = React.useMemo(() => getVariants(!isChromatic()), []);

  return (
    <motion.div
      className={classnames('mb-auto', className)}
      custom={activeStep - previousStep}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
    >
      {children}
    </motion.div>
  );
};
